/** ACC's issue vocabulary, shared by transport adapters and the ACC editor. */
export type AutodeskIssueStatus = "draft" | "open" | "pending" | "in_review" | "closed";
export type AutodeskIssueLocalKind = "observation" | "task" | "punch";

/** Preserve existing local aliases while retaining every ACC status. */
export const AUTODESK_STATUS_FROM_REMOTE: Readonly<
  Record<AutodeskIssueStatus, Record<AutodeskIssueLocalKind, string>>
> = {
  draft: { observation: "draft", task: "draft", punch: "draft" },
  open: { observation: "initiated", task: "initiated", punch: "open" },
  pending: { observation: "not_accepted", task: "pending", punch: "pending" },
  in_review: { observation: "ready_for_review", task: "ready_for_review", punch: "ready_for_review" },
  closed: { observation: "closed", task: "closed", punch: "closed" },
};

export const AUTODESK_ISSUE_STATUS_LABELS: Readonly<Record<AutodeskIssueStatus, string>> = {
  draft: "Draft", open: "Open", pending: "Pending", in_review: "In review", closed: "Closed",
};
export const AUTODESK_ISSUE_STATUS_OPTIONS = Object.values(AUTODESK_ISSUE_STATUS_LABELS);

/** Existing publish defaults stay open; an explicit ACC draft stays draft. */
export function mapAutodeskStatus(status: string | undefined): AutodeskIssueStatus {
  switch ((status ?? "").trim().toLowerCase()) {
    case "draft": return "draft";
    case "pending":
    case "not_accepted": return "pending";
    case "in_review":
    case "ready_for_review": return "in_review";
    case "completed":
    case "closed":
    case "voided": return "closed";
    default: return "open";
  }
}

/** Unknown remote values stay unknown instead of becoming an open issue. */
export function autodeskStatusIfRecognized(value: unknown): AutodeskIssueStatus | undefined {
  if (typeof value !== "string") return undefined;
  const normalized = value.trim().toLowerCase();
  return Object.prototype.hasOwnProperty.call(AUTODESK_STATUS_FROM_REMOTE, normalized)
    ? normalized as AutodeskIssueStatus
    : undefined;
}

export function invertAutodeskStatus(value: unknown, kind: AutodeskIssueLocalKind): string | undefined {
  const remote = autodeskStatusIfRecognized(value);
  return remote === undefined ? undefined : AUTODESK_STATUS_FROM_REMOTE[remote][kind];
}

/** Strict inverse for comparing a deserialized local value with an ACC echo. */
export function autodeskStatusForInvertedStatus(value: unknown): AutodeskIssueStatus | undefined {
  if (typeof value !== "string") return undefined;
  const normalized = value.trim().toLowerCase();
  for (const remote of Object.keys(AUTODESK_STATUS_FROM_REMOTE) as AutodeskIssueStatus[]) {
    if (Object.values(AUTODESK_STATUS_FROM_REMOTE[remote]).includes(normalized)) return remote;
  }
  return undefined;
}

export function formatAutodeskIssueStatus(value: unknown): string | undefined {
  const remote = autodeskStatusIfRecognized(value) ?? autodeskStatusForInvertedStatus(value);
  return remote === undefined ? undefined : AUTODESK_ISSUE_STATUS_LABELS[remote];
}

/** Only the finite ACC choices can become an ACC edit. */
export function parseAutodeskIssueStatusChoice(value: string, kind: AutodeskIssueLocalKind): string {
  const remote = autodeskStatusIfRecognized(value.trim().replace(/\s+/g, "_"));
  if (!remote) throw new Error(`Unsupported ACC status: ${value}. Choose an available ACC status.`);
  return AUTODESK_STATUS_FROM_REMOTE[remote][kind];
}
